import {DefaultPageProps} from "@models/DefaultProps.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {IGetEmpresasFilter} from "@components/empresas/endpoints/filters/IGetEmpresaFilter.ts";
import React, {useEffect, useState} from "react";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {useEmpresa} from "@components/empresas/hooks/useEmpresa.ts";
import {IEmpresa} from "@models/empresa/IEmpresa.ts";
import {SearchTextField} from "@components/shared/textField/SearchTextField.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {EmpresasFilterComponent} from "@components/empresas/EmpresasFilterComponent.tsx";
import {
    ButtonGroup,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material'
import {fullHeight} from "@models/helpers.ts";
import {CreateEmpresaComponent} from "@components/empresas/CreateEmpresaComponent.tsx";
import {EmpresaInfoRow} from "@components/empresas/info/EmpresaInfoRow.tsx";
import {MoreHoriz} from "@mui/icons-material";
import {useContextMenu} from "@contexts/hooks/useContextMenu.ts";
import {ImportEmpresaComponent} from "@components/empresas/ImportEmpresasComponent.tsx";
import {PermissionableButton} from "@shared/specialComponents/PermissionableButton.tsx";

interface EmpresasPageProps extends DefaultPageProps<IGetEmpresasFilter> {
}

export function EmpresasPage(props: EmpresasPageProps) {
    const {getEmpresas} = useEmpresa()
    const {setContextOptions} = useContextMenu()
    const {addPopUp, closePopUp} = useAlert()
    const [empresas, setEmpresas] = useState<IEmpresa[]>([])
    const [filters, setFilters] = useState<IGetEmpresasFilter>(props.filtersProps?.filters ?? {});
    const [pagination, setPagination] = useState<IPagination>(props.pageProps?.pagination ?? {page: 0, pageSize: 10});

    useEffect(() => {
        if (props.filtersProps?.filters) {
            setFilters({...filters, ...props.filtersProps.filters});
        }

    }, [props.filtersProps?.filters]);

    useEffect(() => {
        props.filtersProps?.setFilters({...props.filtersProps.filters, ...filters});
    }, [filters]);

    useEffect(() => {
        props.pageProps?.setPagination({...pagination});
    }, [pagination]);

    useEffect(() => {
        if (props.pageProps?.pagination) {
            setPagination({...pagination, ...props.pageProps.pagination});
        }
    }, [props.pageProps?.pagination]);

    useEffect(() => {
        if (pagination.page <= 0) {
            setPagination(prev => ({...prev, page: 1}))
            return;
        }
        setPagination(prev => ({...prev, loading: true}))
        getEmpresas({filters, pagination}).then(x => {
                if (x.items != null) {
                    setEmpresas(x.items)
                }
                setPagination(prev => ({...prev, ...x.pagination}))
            }
        ).finally(() => {
            setPagination(prev => ({...prev, loading: false, ignoreCache: false}))
        })

    }, [pagination.page, pagination.ignoreCache == true]);

    const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
        setContextOptions({
            event: e,
            anchor: e.currentTarget,
            options: [
                // {label: 'Atualizar', onClick: () => setPagination(prev => ({...prev}))}
            ]
        })
    }

    return (
        <DefaultContainer {...props}
                          pageProps={{pagination, setPagination}}
                          UpperComponent={<Grid container spacing={1} justifyContent={'space-between'}>
                              <Grid size={{xs: 12, md: 6}}>
                                  <SearchTextField value={filters.nome ?? ''}
                                                   hasChanges={JSON.stringify(filters) != ""}
                                                   label={'Nome da empresa'}
                                                   onChange={(e) => {
                                                       setFilters(prev => ({
                                                           ...prev, nome: e.target.value,
                                                       }))
                                                   }}
                                                   openFilters={() => {
                                                       addPopUp({
                                                           title: 'Filtros',
                                                           content: <EmpresasFilterComponent filters={filters}
                                                                                             setFilters={setFilters}
                                                                                             onApply={() => {
                                                                                                 closePopUp()
                                                                                                 setPagination(prev => ({
                                                                                                     ...prev,
                                                                                                     page: 0
                                                                                                 }))
                                                                                             }}/>
                                                       })
                                                   }}
                                                   onSearch={() => {
                                                       setPagination(prev => ({
                                                           ...prev,
                                                           page: 0
                                                       }))
                                                   }}

                                  />
                              </Grid>
                              <Grid size={{xs: 12, md: 6}} component={ButtonGroup} fullWidth sx={{...fullHeight}}>
                                  <PermissionableButton permission={['CriarEmpresa']}
                                                        onClick={() => {
                                                            addPopUp({
                                                                title: 'Importar empresas',
                                                                content: <ImportEmpresaComponent onCreate={() => {
                                                                    closePopUp()
                                                                    setPagination(prev => ({...prev, page: 0}))
                                                                }}/>,
                                                                size: 'lg'
                                                            })
                                                        }}
                                                        showEvenWithoutAccess
                                                        label={'Importar'}/>
                                  <PermissionableButton permission={['CriarEmpresa']}
                                                        onClick={() => {
                                                            addPopUp({
                                                                title: 'Criar nova empresa',
                                                                content: <CreateEmpresaComponent onCreate={() => {
                                                                    closePopUp()
                                                                    setPagination(prev => ({...prev, page: 0}))
                                                                }}/>,
                                                                size: 'lg'
                                                            })
                                                        }}
                                                        showEvenWithoutAccess
                                                        label={'Criar'}/>
                              </Grid>
                          </Grid>}>
            <Grid size={12} component={TableContainer}>
                <Table stickyHeader size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Id</TableCell>
                            <TableCell>Filial</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>Cnpj</TableCell>
                            <TableCell align={'right'}>Mais...</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {empresas.map((empresa) => (
                            <EmpresaInfoRow value={empresa} key={empresa.nroFilial}
                                            EndComponent={(_empresa) => {
                                                return <IconButton disabled onClick={(e) => {
                                                    handleContextMenu(e)
                                                }}>
                                                    <MoreHoriz/>
                                                </IconButton>
                                            }}
                            />
                        ))}
                    </TableBody>
                </Table>
            </Grid>
        </DefaultContainer>
    )
}